import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import "./CloudInfrastructure.css";
import Testimonial from "../../../Testimonial/Testimonial";

const CloudInfrastructure = () => {
  const techItemsRow1 = [
    { name: "Angular.js", logo: "/logos/angular.png" },
    { name: "HTML", logo: "/logos/html.png" },
    { name: "CSS3", logo: "/logos/css.png" },
    { name: "Bootstrap", logo: "/logos/bootstrap.png" },
    { name: "Javascript", logo: "/logos/javascript.png" },
    { name: "AmazonDB", logo: "/logos/amazon_dynamodb.png" },
    { name: "Appian", logo: "/logos/appian.png" },
    { name: "Laravel", logo: "/logos/laravel.png" },
    { name: "MariaDB", logo: "/logos/mariadb.png" },
    { name: "MongoDB", logo: "/logos/mongodb.png" },
    { name: "Android", logo: "/logos/android-5.png" },
    { name: "ERC-20", logo: "/logos/erc-20.png" },
    { name: "Ethereum", logo: "/logos/ethereum.png" },
    { name: "Flutter", logo: "/logos/flutter.png" },
    { name: "Ionic", logo: "/logos/ionic.png" },
    { name: "Augmented Reality", logo: "/logos/Augmented Reality.png" },
    { name: "Crda", logo: "/logos/crda.png" },
    { name: "Data Science", logo: "/logos/data-science.png" },
    { name: "Go Chain", logo: "/logos/gochain.png" },
  ];

  const techItemsRow2 = [
    { name: "Sass", logo: "/logos/saas.png" },
    { name: "JQuery", logo: "/logos/jquery.png" },
    { name: "Less", logo: "/logos/less.png" },
    { name: "Codeigniter", logo: "/logos/codeignitor.png" },
    { name: "Django", logo: "/logos/django.png" },
    { name: "Node JS", logo: "/logos/node js.png" },
    { name: "PHP", logo: "/logos/php.png" },
    { name: "Salesforce", logo: "/logos/salesforce.png" },
    { name: "SQL Server", logo: "/logos/sql-server.png" },
    { name: "MySQL", logo: "/logos/mysql-5.png" },
    { name: "IOS", logo: "/logos/ios.png" },
    { name: "Oracle 6", logo: "/logos/oracle.png" },
    { name: "PostgreSQL", logo: "/logos/postgresql.png" },
    { name: "React Native", logo: "/logos/react_native.png" },
    { name: "Solidity", logo: "/logos/Solidity.png" },
    { name: "Hyperledger", logo: "/logos/Hyperledger.png" },
    { name: "Internet Of Things", logo: "/logos/iot.png" },
    { name: "One Ledger", logo: "/logos/OneLedger.png" },
    { name: "Virtual Reality", logo: "/logos/virtual-reality.png" },
  ];

  const duplicatedRow1 = [...techItemsRow1, ...techItemsRow1];
  const duplicatedRow2 = [...techItemsRow2, ...techItemsRow2];

  const containerRef = useRef(null);
  const row1Ref = useRef(null);
  const row2Ref = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [offset, setOffset] = useState(0);
  const animationRef = useRef();

  useEffect(() => {
    // Set initial container width
    if (containerRef.current) {
      setContainerWidth(containerRef.current.offsetWidth);
    }

    const handleResize = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const scrollSpeed = 1; // Adjust speed as needed (pixels per frame)

    const animate = () => {
      setOffset((prevOffset) => {
        const maxOffset = row1Ref.current?.scrollWidth / 2 || 0;
        const newOffset = prevOffset + scrollSpeed;
        return newOffset >= maxOffset ? 0 : newOffset;
      });
      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(animationRef.current);
    };
  }, [containerWidth]);

  return (
    <div>
      <Helmet>
        <title>Cloud Infrastructure Solutions | Enterprise Cloud Technologies</title>
        <meta 
          name="description" 
          content="Explore our cloud infrastructure expertise and comprehensive technology stack for building scalable, secure, and reliable cloud solutions." 
        />
        <meta 
          name="keywords" 
          content="cloud infrastructure, cloud computing, cloud solutions, AWS, Azure, Google Cloud, cloud migration, cloud security, cloud technologies, enterprise cloud" 
        />
      </Helmet>
          <div className="cloud-infra-container">
      <div className="cloud-hero-section">
        <div className="cloud-hero-content">
          <h1 className="cloud-main-title">Cloud & Infrastructure Solutions</h1>
          <p className="cloud-subtitle">Scalable, secure, and innovative cloud infrastructure for your business growth</p>
          <a href="/contact-us"><button className="cloud-cta-button">Get Started</button></a>
        </div>
        <div className="cloud-hero-animation">
          <div className="floating-cloud cloud-1"></div>
          <div className="floating-cloud cloud-2"></div>
          <div className="floating-cloud cloud-3"></div>
          <div className="server-rack"></div>
        </div>
      </div>

      <div className="cloud-services-section">
        <h2 className="services-title">Our Cloud Services</h2>
        <div className="services-grid">
          <div className="service-card">
            <div className="service-icon cloud-migration"></div>
            <h3>Cloud Migration</h3>
            <p>Seamless transition to cloud environments with minimal disruption to your operations.</p>
          </div>
          <div className="service-card">
            <div className="service-icon cloud-security"></div>
            <h3>Cloud Security</h3>
            <p>Comprehensive security solutions to protect your data and applications in the cloud.</p>
          </div>
          <div className="service-card">
            <div className="service-icon hybrid-cloud"></div>
            <h3>Hybrid Cloud Solutions</h3>
            <p>Optimized infrastructure combining public and private cloud environments.</p>
          </div>
          <div className="service-card">
            <div className="service-icon cloud-optimization"></div>
            <h3>Cloud Optimization</h3>
            <p>Cost-effective solutions to maximize performance while minimizing cloud expenses.</p>
          </div>
        </div>
      </div>

      <div className="infrastructure-section">
        <h2 className="infrastructure-title">Infrastructure Management</h2>
        <div className="infrastructure-content">
          <div className="infrastructure-text">
            <h3>Modern Infrastructure Solutions</h3>
            <p>Our infrastructure services provide the foundation for your digital transformation journey. We design, implement, and manage robust IT infrastructure that supports your business objectives.</p>
            <ul className="infrastructure-list">
              <li>Server management and maintenance</li>
              <li>Network design and implementation</li>
              <li>Data storage solutions</li>
              <li>Disaster recovery planning</li>
              <li>24/7 monitoring and support</li>
            </ul>
          </div>
          <div className="infrastructure-visual">
            <div className="data-center-animation">
              <div className="server-blade"></div>
              <div className="server-blade"></div>
              <div className="server-blade"></div>
              <div className="network-line"></div>
              <div className="data-point"></div>
              <div className="data-point"></div>
              <div className="data-point"></div>
            </div>
          </div>
        </div>
      </div>

      <div className="cloud-benefits-section">
        <h2 className="benefits-title">Benefits of Our Cloud Solutions</h2>
        <div className="benefits-container">
          <div className="benefit-item">
            <div className="benefit-number">01</div>
            <h3>Scalability</h3>
            <p>Easily scale your resources up or down based on demand</p>
          </div>
          <div className="benefit-item">
            <div className="benefit-number">02</div>
            <h3>Cost Efficiency</h3>
            <p>Reduce IT costs with pay-as-you-go models and optimized resources</p>
          </div>
          <div className="benefit-item">
            <div className="benefit-number">03</div>
            <h3>Enhanced Security</h3>
            <p>Enterprise-grade security protocols to protect your data</p>
          </div>
          <div className="benefit-item">
            <div className="benefit-number">04</div>
            <h3>Business Continuity</h3>
            <p>Ensure operations continue with reliable backup and disaster recovery</p>
          </div>
        </div>
      </div>

      <div className="cloud-cta-section">
        <h2>Ready to Transform Your Infrastructure?</h2>
        <p>Contact us today to discuss how our cloud solutions can benefit your business</p>
        <a
  href="https://wa.me/919155599968?text=Hi!%20I%20am%20interested%20in%20Cloud%20Infrastructure%20solutions%20(deployment%2C%20migration%2C%20and%20security).%20Please%20connect%20me%20with%20your%20experts."
  target="_blank"
  rel="noreferrer"
>
  <button className="cloud-contact-button">Contact Our Experts</button>
</a>

      </div>
    </div>
      <Testimonial />

      <div className="tech-slider-container" ref={containerRef}>
        <h2 className="tech-slider-title">Tools & Tech Stack</h2>
        <div className="tech-slider-divider"></div>

        {/* First row */}
        <div className="tech-slider-wrapper">
          <div
            className="tech-slider-row"
            ref={row1Ref}
            style={{ transform: `translateX(-${offset}px)` }}
          >
            {duplicatedRow1.map((tech, index) => (
              <div key={`row1-${index}`} className="tech-slide">
                <img
                  src={tech.logo}
                  alt={tech.name}
                  className="tech-logo"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = "/logos/default.png";
                  }}
                />
                <span className="tech-name">{tech.name}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Second row */}
        <div className="tech-slider-wrapper">
          <div
            className="tech-slider-row"
            ref={row2Ref}
            style={{ transform: `translateX(-${offset}px)` }}
          >
            {duplicatedRow2.map((tech, index) => (
              <div key={`row2-${index}`} className="tech-slide">
                <img
                  src={tech.logo}
                  alt={tech.name}
                  className="tech-logo"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = "/logos/default.png";
                  }}
                />
                <span className="tech-name">{tech.name}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CloudInfrastructure;